/**
 * The words on an app's access list for a person who would be refused
 * (LO-210).
 *
 * `GET /apps/{app}/blocked-users` is the kernel's answer to "who on this list
 * would see the missing-permissions screen instead of the app, and what do
 * they lack": `{count, items: [{user, missing: [{type, tag, access}]}]}`. This
 * file turns that into sentences and nothing else. It decides nothing — which
 * tags exist, who lacks them and whether a request is pending all come from
 * the kernel — so a tag added there is marked here with no change.
 *
 * Plain JavaScript, no React, so the sentences can be held to what they must
 * say by a test that runs in a second.
 */

const clean = (value) => String(value ?? "").trim();
const lower = (value) => clean(value).toLowerCase();

/**
 * The missing tags, each once, in the order the kernel listed them, with what
 * each is needed for: `[{tag, needs: [{type, access}]}]`.
 *
 * An entry with no tag is skipped. A mark that said "no " and nothing would be
 * worse than a mark that named nothing.
 */
export function missingTags(missing) {
  const byTag = new Map();
  for (const entry of Array.isArray(missing) ? missing : []) {
    const tag = clean(entry?.tag);
    if (!tag) continue;
    if (!byTag.has(tag)) byTag.set(tag, { tag, needs: [] });
    const type = clean(entry.type);
    if (type) byTag.get(tag).needs.push({ type, access: clean(entry.access) || "read" });
  }
  return [...byTag.values()];
}

/** `no finance` or `no finance, no hr`. */
export function lackSentence(missing) {
  return missingTags(missing).map((m) => `no ${m.tag}`).join(", ");
}

/** The whole mark. Says what the person would meet and what they lack. */
export function markSentence(missing) {
  const lack = lackSentence(missing);
  return `Would see the missing-permissions screen${lack ? `: ${lack}` : ""}`;
}

const list = (items) =>
  items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/**
 * What one tag is needed for, from the types and the kind of access the
 * kernel named: `finance, to read Payment and Invoice and to write Invoice`.
 */
export function needsSentence(entry) {
  const reads = (entry?.needs || []).filter((n) => n.access !== "write").map((n) => n.type);
  const writes = (entry?.needs || []).filter((n) => n.access === "write").map((n) => n.type);
  const parts = [];
  if (reads.length) parts.push(`to read ${list(reads)}`);
  if (writes.length) parts.push(`to write ${list(writes)}`);
  return parts.length ? `${entry.tag}, ${parts.join(" and ")}` : entry?.tag || "";
}

/**
 * The line at the top of the list. It says what the number is of: this list,
 * or the organization when the app is open to everyone and there is no list.
 * `total` is the people on the list, when there is one.
 */
export function countSentence({ count, total, listed }) {
  const n = Number(count) || 0;
  if (n === 0) {
    return listed ? "Everyone on this list holds what this app needs"
      : "Everyone holds what this app needs";
  }
  if (listed && Number.isFinite(Number(total)) && Number(total) > 0) {
    return `${n} of ${total} ${Number(total) === 1 ? "person" : "people"} on this list can't use this app yet`;
  }
  return `${n} ${n === 1 ? "person" : "people"} in this organization can't use this app yet`;
}

/** People who would be refused, by lower-cased address. */
export function indexBlocked(items) {
  const index = new Map();
  for (const item of Array.isArray(items) ? items : []) {
    const user = lower(item?.user);
    if (user) index.set(user, item);
  }
  return index;
}

/** This person's pending request for this tag, or null. */
export function pendingFor(requests, user, tag) {
  const who = lower(user);
  return (Array.isArray(requests) ? requests : []).find((r) =>
    r?.state === "pending" && lower(r.requested_by) === who && clean(r.tag) === clean(tag)) || null;
}
